import React, { useReducer, useCallback } from 'react'
import { Input, Button } from 'reactstrap'
import LocaleUtils from '~/reactapp/shared/DatePicker/localeUtils'
import { addSeconds, addHours, addMinutes, toDate } from 'date-fns'
import Arrow from './Arrow'
import { TimeUnit, getPmFrom24Hour, isValidTimeUnit } from './timeUnit'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const css = require('./TimePicker.scss')

enum Actions {
  SET_HOURS,
  SET_MINUTES,
  SET_DATE,
}

// shamelessly taken from angular bootstrap time picker
const toggleAmPm = (date: Date) =>
  addSeconds(date, 12 * 60 * (date.getHours() < 12 ? 60 : -60))

export interface TimePickerProps {
  /**
   * Date obj to render into the timepicker
   */
  value: Date | null
  /**
   * Callback invoked when user changes the time
   */
  onChange: Function
  /**
   * Display time in 24 hour or 12 hour clock (useful for i18n)
   */
  use24Hour?: boolean
}

/**
 * Controlled component for selecting the time component of a Date obj
 */
export default function TimePicker({
  value,
  onChange,
  use24Hour = false,
}: TimePickerProps) {
  const date = value || LocaleUtils.relativeNow()
  const hourUnit = use24Hour ? TimeUnit.HOUR_24 : TimeUnit.HOUR_12

  const onChangeRef = React.useRef(onChange)
  React.useLayoutEffect(() => {
    onChangeRef.current = onChange
  })

  const [state, dispatch] = useReducer(
    useCallback(
      (state, action) => {
        switch (action.type) {
          case Actions.SET_HOURS:
            return { ...state, hourTxt: action.payload }
          case Actions.SET_MINUTES:
            return { ...state, minTxt: action.payload }
          case Actions.SET_DATE:
            return {
              date: action.payload,
              hourTxt: LocaleUtils.cleanFormat(
                action.payload,
                use24Hour ? 'H' : 'h'
              ),
              minTxt: LocaleUtils.cleanFormat(action.payload, 'mm'),
              isPm: getPmFrom24Hour(action.payload.getHours()),
            }
          default:
            return state
        }
      },
      [use24Hour]
    ),
    {
      date,
      hourTxt: LocaleUtils.cleanFormat(date, use24Hour ? 'H' : 'h'),
      minTxt: LocaleUtils.cleanFormat(date, 'mm'),
      isPm: getPmFrom24Hour(date.getHours()),
    }
  )

  const firstUpdate = React.useRef(true)
  React.useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }

    onChangeRef.current(state.date)
  }, [state.date])

  const incrementTime = (unit: TimeUnit) =>
    dispatch({
      type: Actions.SET_DATE,
      payload:
        unit === TimeUnit.MINUTE
          ? addMinutes(state.date, 1)
          : addHours(state.date, 1),
    })

  const decrementTime = (unit: TimeUnit) =>
    dispatch({
      type: Actions.SET_DATE,
      payload:
        unit === TimeUnit.MINUTE
          ? addMinutes(state.date, -1)
          : addHours(state.date, -1),
    })

  const inputKeyDownHandler = (unit: TimeUnit) => (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    switch (e.key) {
      case 'ArrowUp':
        incrementTime(unit)
        break
      case 'ArrowDown':
        decrementTime(unit)
        break
    }
  }

  const changeHandler = (unit: TimeUnit) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const text = e.target.value
    const isValid = /^\d{0,2}$/.test(text)

    if (isValid) {
      switch (unit) {
        case TimeUnit.HOUR_24:
        case TimeUnit.HOUR_12:
          dispatch({ type: Actions.SET_HOURS, payload: text })
          break
        case TimeUnit.MINUTE:
          dispatch({ type: Actions.SET_MINUTES, payload: text })
          break
      }
    }
  }

  const blurHandler = (unit: TimeUnit) => (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    const value = parseInt(e.target.value, 10)
    const date = toDate(state.date)

    if (isValidTimeUnit(unit, value)) {
      switch (unit) {
        case TimeUnit.HOUR_12: {
          const newHour = value === 12 ? 0 : value
          date.setHours(state.isPm ? newHour + 12 : newHour)
          break
        }
        case TimeUnit.HOUR_24:
          date.setHours(value)
          break
        case TimeUnit.MINUTE:
          date.setMinutes(value)
          break
      }
    }
    dispatch({ type: Actions.SET_DATE, payload: date })
  }

  return (
    <div className={`${css.timepicker} bootstrap4`}>
      <div className="inputWithArrow">
        <div>
          <Arrow
            direction="up"
            unit={hourUnit}
            onClick={() => incrementTime(hourUnit)}
          />
          <Arrow
            direction="up"
            unit={TimeUnit.MINUTE}
            onClick={() => incrementTime(TimeUnit.MINUTE)}
          />
        </div>
        <div className="inputRow">
          <Input
            className="input"
            value={state.hourTxt}
            onKeyDown={inputKeyDownHandler(hourUnit)}
            onChange={changeHandler(hourUnit)}
            onBlur={blurHandler(hourUnit)}
            data-testid={'timepicker-hour'}
          />
          <span className="inputDivider">:</span>
          <Input
            className="input"
            value={state.minTxt}
            onKeyDown={inputKeyDownHandler(TimeUnit.MINUTE)}
            onChange={changeHandler(TimeUnit.MINUTE)}
            onBlur={blurHandler(TimeUnit.MINUTE)}
            data-testid={'timepicker-minute'}
          />
        </div>
        <div>
          <Arrow
            direction="down"
            unit={hourUnit}
            onClick={() => decrementTime(hourUnit)}
          />
          <Arrow
            direction="down"
            unit={TimeUnit.MINUTE}
            onClick={() => decrementTime(TimeUnit.MINUTE)}
          />
        </div>
      </div>
      {!use24Hour && (
        <Button
          className="ampm"
          onClick={() =>
            dispatch({
              type: Actions.SET_DATE,
              payload: toggleAmPm(state.date),
            })
          }
        >
          {state.isPm ? 'PM' : 'AM'}
        </Button>
      )}
    </div>
  )
}
